import { useEffect } from "react";
import "@/App.css";
import {
  BrowserRouter,
  Routes,
  Route,
  useLocation,
  Navigate,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { Toaster } from "@/components/ui/sonner";
import { api } from "@/lib/api";

import Landing from "@/pages/Landing";
import Marketplace from "@/pages/Marketplace";
import DossierDetail from "@/pages/DossierDetail";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import Dashboard from "@/pages/Dashboard";
import NouveauDossier from "@/pages/NouveauDossier";
import CallPage from "@/pages/CallPage";
import Admin from "@/pages/Admin";
import Profile from "@/pages/Profile";
import Account from "@/pages/Account";
import Charte from "@/pages/Charte";
import CGU from "@/pages/CGU";
import { ScrollToHash } from "@/components/ScrollToHash";
import { AppErrorBoundary } from "@/components/AppErrorBoundary";
import PrivateRouteSeo from "@/components/PrivateRouteSeo";
import { Helmet, HelmetProvider } from "react-helmet-async";

const AuthCallback = () => {
  const navigate = useNavigate();
  useEffect(() => {
    const hash = window.location.hash;
    const match = hash.match(/session_id=([^&]+)/);
    if (!match) {
      navigate("/login", { replace: true });
      return;
    }
    const session_id = match[1];
    (async () => {
      try {
        const { data } = await api.post("/auth/session", { session_id });
        if (data?.session_token) {
          localStorage.setItem("lm_token", data.session_token);
        }
        window.location.hash = "";
        const dest = data?.user?.role === "admin" ? "/admin" : "/marketplace";
        navigate(dest, { replace: true, state: { user: data.user } });
      } catch {
        navigate("/login", { replace: true });
      }
    })();
  }, [navigate]);
  return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="text-slate-500 text-sm">Connexion en cours…</div>
    </div>
  );
};

const ProtectedRoute = ({ children, adminOnly = false }) => {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center text-slate-500">Chargement…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (adminOnly && user.role !== "admin") return <Navigate to="/marketplace" replace />;
  return children;
};

/** Accès à la page appel : session Supabase ou lien QR signé (?h=…). */
const CallAccessRoute = () => {
  const { transactionId } = useParams();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const { user, loading } = useAuth();
  const h = searchParams.get("h");
  if (typeof window !== "undefined" && h && transactionId) {
    try {
      window.sessionStorage.setItem(`rdm_call_h_${transactionId}`, h);
    } catch {
      /* ignore */
    }
  }
  let stored = null;
  try {
    stored = typeof window !== "undefined" && transactionId ? window.sessionStorage.getItem(`rdm_call_h_${transactionId}`) : null;
  } catch {
    stored = null;
  }
  const hasHandoff = Boolean(h || stored);
  if (loading) return <div className="min-h-screen flex items-center justify-center text-slate-500">Chargement…</div>;
  if (!user && !hasHandoff) {
    return (
      <Navigate
        to="/login"
        replace
        state={{ from: { pathname: location.pathname, search: location.search } }}
      />
    );
  }
  return <CallPage />;
};

const AppRouter = () => {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <>
      <PrivateRouteSeo pathname={location.pathname} />
      <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/marketplace" element={<Marketplace />} />
      <Route path="/catalogue" element={<Navigate to="/marketplace" replace />} />
      <Route path="/dossier/:id" element={<DossierDetail />} />
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/charte" element={<Charte />} />
      <Route path="/cgu" element={<CGU />} />
      <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
      <Route path="/compte" element={<ProtectedRoute><Account /></ProtectedRoute>} />
      <Route path="/nouveau-dossier" element={<NouveauDossier />} />
      <Route path="/call/:transactionId/m" element={<CallAccessRoute />} />
      <Route path="/call/:transactionId" element={<CallAccessRoute />} />
      <Route path="/admin" element={<ProtectedRoute adminOnly><Admin /></ProtectedRoute>} />
      <Route path="/profil/:userId" element={<Profile />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
    </>
  );
};

function App() {
  return (
    <HelmetProvider>
      <div className="App">
        <Helmet>
          {/*
            Renforce l’icône d’onglet après hydratation (certaines configs CDN / routes SPA).
            Même ressource que index.html, PUBLIC_URL = '' en hébergement à la racine.
          */}
          <link rel="icon" href={`${process.env.PUBLIC_URL || ""}/favicon.ico?v=2`} sizes="32x32" />
          <link rel="shortcut icon" href={`${process.env.PUBLIC_URL || ""}/favicon.ico?v=2`} />
          <link rel="icon" type="image/svg+xml" sizes="any" href={`${process.env.PUBLIC_URL || ""}/favicon.svg?v=2`} />
          <link rel="icon" type="image/png" sizes="192x192" href={`${process.env.PUBLIC_URL || ""}/rdvmarket-icon.png?v=2`} />
          <link rel="apple-touch-icon" href={`${process.env.PUBLIC_URL || ""}/rdvmarket-icon.png?v=2`} sizes="180x180" />
        </Helmet>
        <a
          href="#contenu-principal"
          className="sr-only focus:not-sr-only focus:fixed focus:left-4 focus:top-4 focus:z-[100] focus:rounded-md focus:bg-white focus:px-4 focus:py-3 focus:text-sm focus:font-semibold focus:text-[#0B0D12] focus:shadow-lg focus:ring-2 focus:ring-[#2D7FF9] focus:outline-none"
        >
          Aller au contenu principal
        </a>
        <AuthProvider>
          <BrowserRouter>
            <ScrollToHash />
            <main id="contenu-principal" tabIndex={-1} className="outline-none">
              <AppErrorBoundary>
                <AppRouter />
              </AppErrorBoundary>
            </main>
            <Toaster richColors position="top-right" />
          </BrowserRouter>
        </AuthProvider>
      </div>
    </HelmetProvider>
  );
}

export default App;
